import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { differenceInCalendarDays, format } from "date-fns";
import { Box, Chip, CircularProgress, Grid2, Popper } from "@mui/material";
import { Typography } from "theme";
import { BookingsResponse, IBooking } from "../../../api/hooks/useBookings/type.ts";
import { FC, useState } from "react";
import { Edit, Information, Warning } from "theme/icons";
import { generatePath, useNavigate } from "react-router-dom";
import { Routes } from "router";

const columns: GridColDef<IBooking>[] = [
  { field: "bookingId", headerName: "ID", width: 60, sortable: false },
  { field: "name", headerName: "І'мя", valueGetter: (_, row) => row.clientName, flex: 1, sortable: false },
  { field: "phone", headerName: "Телефон", valueGetter: (_, row) => row.clientPhoneNumber, flex: 1, sortable: false },
  { field: "title", headerName: "Зал", valueGetter: (_, row) => row.hallName, flex: 1, sortable: false },
  {
    field: "date",
    headerName: "Дата бронювання",
    flex: 1,
    valueGetter: (_, row) => `${format(row.startDate, "dd-MM-yyyy HH:mm")} - ${format(row.endDate, "HH:mm")}`,
    sortable: false
  },
  {
    field: "paymentStatus", headerName: "Статус оплати", flex: 1, renderCell: (params) => {
      switch (params.row.paymentStatus) {
        case "PaymentPending":
          return <Grid2 p={1} height="100%" container flexDirection="row" rowGap={0.5}>
            <Chip label="Очікує оплати" color="warning" />
            <Typography variant="body2" weight="regular">{`Оплачено: ${params.row.alreadyPaid}/${params.row.priceWithDiscounts}₴`}</Typography>
          </Grid2>;
        case "PaymentCompleted":
          return <Grid2 p={1} height="100%" container flexDirection="row" rowGap={0.5}>
            <Chip label="Оплачено" color="success" style={{
              color: "white",
            }} />
            <Typography variant="body2" weight="regular">{`Оплачено: ${params.row.alreadyPaid}/${params.row.priceWithDiscounts}₴`}</Typography>
          </Grid2>;
        case "PaymentMade":
          return <Grid2 p={1} height="100%" container flexDirection="row" rowGap={0.5}>
            <Chip label="Підверджено" style={{
              color: "white",
              backgroundColor:'orange'
            }} />
            <Typography variant="body2" weight="regular">{`Оплачено: ${params.row.alreadyPaid}/${params.row.priceWithDiscounts}₴`}</Typography>
          </Grid2>;
        case "PaymentCanceled":
          return <Grid2 p={1} height="100%" container flexDirection="row" rowGap={0.5}>
            <Chip label="Скасовано" color="error" />
            <Typography variant="body2" weight="regular">{`Сума: ${params.row.priceWithDiscounts}₴`}</Typography>
          </Grid2>;
        default:
          return <Grid2 p={1} height="100%" container flexDirection="row" rowGap={0.5}>
          <Chip label={params.row.paymentStatus} color="warning" />
          <Typography variant="body2" weight="regular">{`Оплачено: ${params.row.alreadyPaid}/${params.row.priceWithDiscounts}₴`}</Typography>
    </Grid2>;
      }
    },
    sortable: false
  }, {
    field: "actions",
    headerName: "",
    width: 150,
    renderCell: (params) => {
      const navigate = useNavigate();
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

      const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
      };

      const open = Boolean(anchorEl);
      const id = open ? 'simple-popper' : undefined;

      return (
        <Grid2 container alignItems="center" gap={1}>
          <div>
            <button
              disabled={differenceInCalendarDays(new Date(), new Date(params.row.startDate)) > 1}
              onClick={() =>
                navigate(
                  generatePath(Routes.BookingById, {
                    id: String(params.row.bookingId),
                  }),
                )
              }
            >
              <Edit />
            </button>
          </div>
          <div>
            <button
              onClick={() =>
                navigate(
                  generatePath(Routes.BookingDetails, {
                    id: String(params.row.bookingId),
                  }),
                )
              }
            >
              <Information />
            </button>
          </div>
          {
            params.row.clientComment && <div>
              <button onClick={handleClick}>
                <Warning />
              </button>
              <Popper id={id} open={open} anchorEl={anchorEl}>
                <Box sx={{ border: 1, p: 1, bgcolor: "background.paper" }}>
                  {params.row.clientComment}
                </Box>
              </Popper>
            </div>
          }
        </Grid2>
      );
    },
    sortable: false
  },
];

type TableProps = {
  data?: BookingsResponse;
  loading: boolean;
  paginationModel: GridPaginationModel;
  setPaginationModel: (model: GridPaginationModel) => void;
}

const Table: FC<TableProps> = ({ data, loading, paginationModel, setPaginationModel }) => {
  if (!data) return <Grid2 container justifyContent="center">
    <CircularProgress color="secondary" />
  </Grid2>;

  return (
    <div>
      <DataGrid
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={row => row.bookingId}
        rows={data.bookings}
        columns={columns}
        rowHeight={70}
        disableColumnMenu
        paginationMode="server"
        rowSelection={false}
        initialState={{ pagination: { rowCount: data.totalCount } }}
        pageSizeOptions={[20, 50]}
      />
    </div>
  );
};

export default Table;
